/**
 * Clinical tools — API types + Query hooks.
 *
 * Two endpoints backing a single hook surface:
 *   - /v1/public/tools        — unauthenticated read-only catalogue
 *   - /v1/tools               — authed catalogue including `is_favourite` join
 *
 * The hooks pick automatically based on auth state. Query keys diverge by
 * authed/public so signing in/out swaps cache cleanly (a stale public
 * response must never survive into the authed session where favourites
 * matter).
 *
 * Response shape mirrors DB column names (snake_case) — kept literal to
 * stay consistent with other pages (alerts, directory) that consume the
 * same convention from the API.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export interface ClinicalTool {
  id: string;
  slug: string;
  name: string;
  description: string;
  reference: string | null;
  reference_url: string | null;
  guideline_version: string | null;
  last_reviewed_at: string | null;
  implementation_key: string;
  tags: string[];
  allowed_roles: string[];
  category_slug: string;
  category_name: string;
  category_sort_order: number;
  /** Only present on the authed `/v1/tools` endpoint; omitted for public. */
  is_favourite?: boolean;
  pinned_at?: string | null;
  favourite_sort_order?: number | null;
}

interface ToolListResponse {
  items: ClinicalTool[];
}

interface UsageEventPayload {
  completed?: boolean;
  outputCopied?: boolean;
  sessionId?: string;
  durationSeconds?: number;
  cityId?: string;
}

const TOOLS_KEY_AUTHED = ["clinical-tools", "authed"] as const;
const TOOLS_KEY_PUBLIC = ["clinical-tools", "public"] as const;

/**
 * Catalogue fetch is deliberately decoupled from auth initialisation.
 *
 * Earlier design waited for `!authLoading` before firing any request, which
 * meant a slow/cold/down Keycloak (e.g. Cloud Run cold start on the auth
 * iframe) would leave the page stuck on the error card — even though the
 * public endpoint needs no auth at all.
 *
 * New behaviour:
 *   1. Fire the public query IMMEDIATELY on mount (no auth wait).
 *   2. Once auth resolves to authenticated, fire the authed query — its
 *      response supersedes the public one (different query key).
 *
 * Both responses coexist in the cache; the component decides which to
 * render based on `isAuthenticated`. This gives:
 *   - Instant first paint for anonymous visitors
 *   - Favourite state appearing seamlessly once auth finishes
 *   - No hard dependency on Keycloak uptime for the public catalogue
 */
export function useClinicalTools() {
  const { isAuthenticated } = useAuth();

  const publicQuery = useQuery({
    queryKey: TOOLS_KEY_PUBLIC,
    queryFn: () => api.get<ToolListResponse>("/v1/public/tools", { skipAuth: true }),
    staleTime: 60_000,
  });

  const authedQuery = useQuery({
    queryKey: TOOLS_KEY_AUTHED,
    queryFn: () => api.get<ToolListResponse>("/v1/tools"),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  // Prefer authed data (has favourite state) when available; otherwise
  // fall back to public. isLoading/error combine across both.
  if (isAuthenticated && authedQuery.data) {
    return authedQuery;
  }
  return publicQuery;
}

export function useClinicalTool(slug: string | undefined) {
  const { isAuthenticated } = useAuth();

  const publicQuery = useQuery({
    queryKey: ["clinical-tool", "public", slug],
    queryFn: () =>
      api.get<ClinicalTool>(`/v1/public/tools/${slug}`, { skipAuth: true }),
    enabled: !!slug,
    staleTime: 60_000,
  });

  const authedQuery = useQuery({
    queryKey: ["clinical-tool", "authed", slug],
    queryFn: () => api.get<ClinicalTool>(`/v1/tools/${slug}`),
    enabled: isAuthenticated && !!slug,
    staleTime: 60_000,
  });

  if (isAuthenticated && authedQuery.data) {
    return authedQuery;
  }
  return publicQuery;
}

/**
 * Toggle favourite — optimistic update against the authed cached list so
 * the heart icon flips instantly. Rollback on error, invalidate on settle.
 * Only callable when authed (the button is disabled otherwise).
 */
export function useToggleFavourite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, favourite }: { slug: string; favourite: boolean }) =>
      api.post(`/v1/tools/${slug}/favourite`, { favourite }),
    onMutate: async ({ slug, favourite }) => {
      await qc.cancelQueries({ queryKey: TOOLS_KEY_AUTHED });
      const previous = qc.getQueryData<ToolListResponse>(TOOLS_KEY_AUTHED);
      if (previous) {
        qc.setQueryData<ToolListResponse>(TOOLS_KEY_AUTHED, {
          items: previous.items.map((t) =>
            t.slug === slug ? { ...t, is_favourite: favourite } : t,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(TOOLS_KEY_AUTHED, context.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: TOOLS_KEY_AUTHED });
    },
  });
}

/**
 * Log a usage event. Fire-and-forget from the caller's perspective —
 * analytics loss on network error is acceptable. Authed only; guard at
 * call sites (or the request will 401 and fall into the silent reject).
 */
export function useLogToolUsage() {
  return useMutation({
    mutationFn: ({ slug, payload }: { slug: string; payload?: UsageEventPayload }) =>
      api.post(`/v1/tools/${slug}/usage`, payload ?? {}),
  });
}
